import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { defaultConfig } from '../constants/defaultConfig';

// Deep merge helper
export function mergeConfig(target, source) {
  const output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target))
          Object.assign(output, { [key]: source[key] });
        else
          output[key] = mergeConfig(target[key], source[key]);
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

function isObject(item) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

export const RestaurantConfigContext = createContext({
    config: defaultConfig,
    loadingConfig: false,
    updateConfig: async () => {},
});

export function RestaurantConfigProvider({ children, restaurantId }) {
    const [config, setConfig] = useState(defaultConfig);
    const [loadingConfig, setLoadingConfig] = useState(true);

    useEffect(() => {
        if (!restaurantId) {
            setLoadingConfig(false);
            return;
        }

        async function fetchConfig() {
            try {
                const { data, error } = await supabase
                    .from('restaurants')
                    .select('config')
                    .eq('id', restaurantId)
                    .single();

                if (error) throw error;

                if (data && data.config) {
                     // Merge fetched config over defaults
                     const merged = mergeConfig(defaultConfig, data.config);
                     setConfig(merged);
                }
            } catch (err) {
                console.error("Error fetching config, falling back to defaults:", err);
            } finally {
                setLoadingConfig(false);
            }
        }
        fetchConfig();
    }, [restaurantId]);

    const updateConfig = useCallback(async (newConfigDetails) => {
        // Deep merge new changes
        const nextConfig = mergeConfig(config, newConfigDetails);
        setConfig(nextConfig);

        try {
            await supabase
                .from('restaurants')
                .update({ config: nextConfig })
                .eq('id', restaurantId);
        } catch (e) {
            console.error("Failed to persist config", e);
        }
    }, [config, restaurantId]);

    return (
        <RestaurantConfigContext.Provider value={{ config, updateConfig, loadingConfig }}>
            {children}
        </RestaurantConfigContext.Provider>
    );
}

export function useConfig() {
    const ctx = useContext(RestaurantConfigContext);
    // Fallback so components outside a provider never crash
    if (!ctx) return { config: defaultConfig, loadingConfig: false, updateConfig: async () => {} };
    return ctx;
}
